import TimeZone from '@cobliteam/api-lib/src/request/Timezone';

export const TIMEZONE_HEADER = 'cobli-timezone';

/**
 * An IANA zone id is either `Area/Location` (`America/Sao_Paulo`, `Europe/Lisbon`) or the bare
 * `UTC`. Anything else — a numeric offset (`-03:00`), an abbreviation (`BRT`), an empty string —
 * is rejected: the AI backend expects an id it can feed to `Intl`, and a header it cannot parse is
 * worse than no header at all (absent falls back correctly server-side).
 */
const isIANATimeZone = (value: unknown): value is string => typeof value === 'string' && (value === 'UTC' || /^[A-Za-z][A-Za-z0-9_+-]*\/[A-Za-z0-9_+\-/]+$/.test(value));

/**
 * Resolve the timezone sent to the AI backend, or `undefined` when it cannot be resolved.
 *
 * There is no fleet/user-level timezone in the dashboard's session data today, so the browser zone
 * is the only source. When a fleet-level setting lands it takes precedence here.
 */
export const resolveTimeZone = (): string | undefined => {
  try {
    const timeZone = TimeZone.getTimezone();
    return isIANATimeZone(timeZone) ? timeZone : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Header bag to spread into any AI-API request. Empty when the zone cannot be resolved, so the
 * header is omitted rather than sent blank.
 */
export const timeZoneHeader = (): Record<string, string> => {
  const timeZone = resolveTimeZone();
  return timeZone ? { [TIMEZONE_HEADER]: timeZone } : {};
};
